import type { FirebaseApp } from 'firebase/app';
import { getAuth } from 'firebase/auth';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { callableReadKey, runKeyedAdmittedCallableRead } from '@kop/callable-read-admission';
import {
  hqMonthlyMeetingAttachmentSchema,
  hqMonthlyMeetingEvidencePreparedSchema,
  hqMonthlyMeetingEvidenceOpenedSchema,
  hqMonthlyMeetingSaveInputSchema,
  hqMonthlyMeetingSaveResultSchema,
  hqMonthlyMeetingsLoadInputSchema,
  hqMonthlyMeetingsLoadResultSchema,
  hqMonthlyMeetingEvidencePrepareSchema,
  hqMonthlyMeetingEvidenceKeySchema,
  hqMonthlyMeetingEvidenceOpenSchema,
  type HqMonthlyMeetingSaveInput,
} from '@kop/contracts';

export function createHqMonthlyMeetingsPortForApp(app: FirebaseApp, expectedUid: string) {
  const auth = getAuth(app),
    functions = getFunctions(app, 'asia-northeast3');
  const current = () => {
    if (!expectedUid || auth.currentUser?.uid !== expectedUid)
      throw Error('HQ_MEETING_AUTH_SESSION_CHANGED');
  };
  const call = async (name: string, payload: unknown, read = false) => {
    await auth.authStateReady();
    current();
    const result = await httpsCallable(functions, name, {
      limitedUseAppCheckTokens: !read,
      timeout: 120000,
    })(payload);
    current();
    return result.data;
  };
  async function evidence(
    operation: 'prepare',
    input: Readonly<Record<string, unknown>>,
  ): Promise<ReturnType<typeof hqMonthlyMeetingEvidencePreparedSchema.parse>>;
  async function evidence(
    operation: 'commit',
    input: Readonly<Record<string, unknown>>,
  ): Promise<ReturnType<typeof hqMonthlyMeetingAttachmentSchema.parse>>;
  async function evidence(
    operation: 'open',
    input: Readonly<Record<string, unknown>>,
  ): Promise<ReturnType<typeof hqMonthlyMeetingEvidenceOpenedSchema.parse>>;
  async function evidence(
    operation: 'prepare' | 'commit' | 'open',
    raw: Readonly<Record<string, unknown>>,
  ) {
    const name = {
      prepare: 'prepareHqMonthlyMeetingEvidence',
      commit: 'commitHqMonthlyMeetingEvidence',
      open: 'openHqMonthlyMeetingEvidence',
    }[operation];
    const input =
      operation === 'prepare'
        ? hqMonthlyMeetingEvidencePrepareSchema.parse(raw)
        : operation === 'commit'
          ? hqMonthlyMeetingEvidenceKeySchema.parse(raw)
          : hqMonthlyMeetingEvidenceOpenSchema.parse(raw);
    const result =
      operation === 'open'
        ? await runKeyedAdmittedCallableRead(
            callableReadKey(expectedUid, name, input),
            () => call(name, input, true),
            undefined,
            'hq-media',
          )
        : await call(name, input);
    return operation === 'prepare'
      ? hqMonthlyMeetingEvidencePreparedSchema.parse(result)
      : operation === 'commit'
        ? hqMonthlyMeetingAttachmentSchema.parse(result)
        : hqMonthlyMeetingEvidenceOpenedSchema.parse(result);
  }
  return {
    async load(raw: Readonly<{ year: number; meetingId?: string }>) {
      const input = hqMonthlyMeetingsLoadInputSchema.parse(raw);
      await auth.authStateReady();
      current();
      const result = await runKeyedAdmittedCallableRead(
        callableReadKey(expectedUid, 'loadHqMonthlyMeetings', input),
        () => call('loadHqMonthlyMeetings', input, true),
      );
      return hqMonthlyMeetingsLoadResultSchema.parse(result);
    },
    async save(raw: HqMonthlyMeetingSaveInput) {
      const input = hqMonthlyMeetingSaveInputSchema.parse(raw);
      return hqMonthlyMeetingSaveResultSchema.parse(await call('saveHqMonthlyMeeting', input));
    },
    evidence,
  };
}
export type HqMonthlyMeetingsPort = ReturnType<typeof createHqMonthlyMeetingsPortForApp>;
